import { Link } from 'react-router-dom';
import { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { ArrowRight, Heart, Shield, Star, Home as HomeIcon, Syringe, PawPrint, Truck, CheckCircle, Phone } from 'lucide-react';
import KittenCard from '@/components/KittenCard';
import SEO from '@/components/SEO';
import GoogleReviews from '@/components/GoogleReviews';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Home() {
  const [featuredKittens, setFeaturedKittens] = useState([]);
  const [heroVisible, setHeroVisible] = useState(false);
  const heroImageRef = useRef(null);

  const fetchFeaturedKittens = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/kittens`);
      setFeaturedKittens(response.data.slice(0, 4));
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to fetch kittens:', error);
      }
    }
  }, []);

  useEffect(() => {
    fetchFeaturedKittens();
  }, [fetchFeaturedKittens]);

  // IntersectionObserver for one-time hero animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setHeroVisible(true);
            observer.disconnect(); // One-time trigger only
          }
        });
      },
      { threshold: 0.2 }
    );

    if (heroImageRef.current) {
      observer.observe(heroImageRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const trustIndicators = [
    { icon: Heart, title: 'Home Raised', subtitle: 'with Love' },
    { icon: Shield, title: 'Veterinary Care', subtitle: '& Vaccinations' },
    { icon: PawPrint, title: 'Healthy & Social', subtitle: 'Kittens' },
    { icon: HomeIcon, title: 'Safe & Clean', subtitle: 'Environment' },
  ];

  const trustSections = [
    {
      icon: HomeIcon,
      title: 'How Our Kittens Are Raised',
      description: 'Our kittens are home raised, well socialized, and surrounded by love and daily attention.',
      link: '/about'
    },
    {
      icon: Shield,
      title: 'Health & Veterinary Care',
      description: 'All kittens receive full veterinary exams, vaccinations, deworming, and health records.',
      link: '/adoption-process'
    },
    {
      icon: CheckCircle,
      title: 'Our Kitten Process',
      description: 'Simple, transparent process to help you bring your new family member home.',
      link: '/faq'
    },
    {
      icon: Truck,
      title: 'Safe Pickup or Delivery',
      description: 'Local pickup available. Delivery options can be discussed.',
      link: '/contact'
    },
  ];

  return (
    <main className="bg-[#FAF8F5]">
      <SEO 
        title="KittenState | Scottish Fold & Scottish Straight Kittens in Pennsylvania"
        description="KittenState is a trusted cattery in Covington Township, PA specializing in Scottish Fold and Scottish Straight kittens. View available kittens, meet our parents, and learn about adoption."
        keywords="Scottish Fold kittens, Scottish Straight kittens, Scottish Fold breeder, Scottish Straight breeder, kitten adoption, Pennsylvania cattery, KittenState"
        url="https://kittenstate.com/"
      />

      {/* Hero Section */}
      <section className="relative lg:min-h-[80vh] flex items-end lg:items-center overflow-hidden" ref={heroImageRef}>
        {/* Desktop: Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-rose-50/80 via-[#FAF8F5] to-[#FAF8F5] hidden lg:block" />
        
        {/* Mobile Hero Layout - Image above, text below */}
        <div className="lg:hidden w-full bg-[#FAF8F5]">
          {/* Mobile Image Container - shows full kittens */}
          <div className={`w-full transition-all duration-[900ms] ease-out ${
            heroVisible 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-3'
          }`}>
            <img
              src="https://customer-assets-jt897jd0.emergentagent.net/job_kitten-reserve-dev/artifacts/fb9d9dfb8949b723_320FE542-5F99-446E-8459-FF9610F8CD80.png"
              alt="Adorable Scottish Fold and Scottish Straight Kittens"
              className="w-full h-auto object-contain"
            />
          </div>
          
          {/* Mobile Text Content - below image */}
          <div className={`px-6 py-8 text-center bg-[#FAF8F5] transition-all duration-[900ms] ease-out delay-150 ${
            heroVisible 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-5'
          }`}>
            <p
              data-testid="hero-title-mobile"
              className="text-2xl sm:text-3xl font-serif font-bold tracking-tight leading-[1.2] text-stone-900 mb-4"
              role="heading"
              aria-level="1"
            >
              Scottish Fold<br />
              & Scottish Straight Kittens<br />
              <span className="text-[#E85D75]">for Loving Homes</span>
            </p>
            
            <p className="text-sm sm:text-base leading-relaxed text-stone-600 mb-6 max-w-sm mx-auto">
              Healthy, socialized kittens raised with love.
            </p>

            <div className="flex flex-col gap-3 max-w-xs mx-auto">
              <Link to="/kittens" className="w-full">
                <Button
                  data-testid="view-available-kittens-button-mobile"
                  className="w-full bg-[#E85D75] text-white hover:bg-[#D64560] shadow-lg shadow-[#E85D75]/25 rounded-full px-6 py-5 text-sm font-medium transition-all"
                >
                  <PawPrint className="w-4 h-4 mr-2" />
                  View Available Kittens
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/contact" className="w-full">
                <Button
                  data-testid="contact-us-button-mobile"
                  variant="outline"
                  className="w-full bg-white text-stone-800 border border-stone-200 hover:border-rose-300 hover:bg-rose-50/30 rounded-full px-6 py-5 text-sm font-medium transition-all"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Desktop Layout - unchanged */}
        <div className="hidden lg:block max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10 w-full">
          {/* Desktop: Custom grid with larger image (55% image, 45% text) */}
          <div className="grid lg:grid-cols-[45fr_55fr] gap-10 items-center">
            {/* Content - left on desktop */}
            <div className={`text-left transition-all duration-[900ms] ease-out delay-200 ${
              heroVisible 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-5'
            }`}>
              {/* Desktop only: Premium tag */}
              <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#E85D75] mb-3">
                Premium Scottish Kittens
              </p>
              
              <h1
                data-testid="hero-title"
                className="text-5xl xl:text-6xl font-serif font-bold tracking-tight leading-[1.15] text-stone-900 mb-5"
              >
                Scottish Fold &<br />
                Scottish Straight Kittens
              </h1>
              
              <p className="text-2xl font-serif text-[#E85D75] mb-4">
                Happy Kittens. Brighter Tomorrows.
              </p>
              
              <p className="text-lg leading-relaxed text-stone-600 mb-6 max-w-md">
                A trusted Scottish Fold breeder and Scottish Straight breeder offering healthy, well-socialized kittens for adoption. Raised with love in a home environment.
              </p>

              <div className="flex gap-4">
                <Link to="/kittens">
                  <Button
                    data-testid="view-available-kittens-button"
                    className="bg-[#E85D75] text-white hover:bg-[#D64560] shadow-lg shadow-[#E85D75]/25 hover:shadow-xl hover:shadow-[#E85D75]/35 rounded-full px-8 py-6 text-base font-medium transition-all transform hover:-translate-y-0.5"
                  >
                    <PawPrint className="w-4 h-4 mr-2" />
                    View Available Kittens
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/about">
                  <Button
                    data-testid="about-cattery-button"
                    variant="outline"
                    className="bg-white text-stone-800 border border-stone-200 hover:border-rose-300 hover:bg-rose-50/30 rounded-full px-8 py-6 text-base font-medium transition-all"
                  >
                    About Our Cattery
                  </Button>
                </Link>
              </div>
            </div>

            {/* Desktop: Hero Image */}
            <div className="relative">
              <div 
                className={`relative rounded-3xl overflow-hidden shadow-2xl shadow-stone-200/50 transition-all duration-[900ms] ease-out ${
                  heroVisible 
                    ? 'opacity-100 translate-y-0' 
                    : 'opacity-0 translate-y-5'
                }`}
              >
                <img
                  src="https://customer-assets-jt897jd0.emergentagent.net/job_kitten-reserve-dev/artifacts/fb9d9dfb8949b723_320FE542-5F99-446E-8459-FF9610F8CD80.png"
                  alt="Adorable Scottish Fold and Scottish Straight Kittens"
                  className="w-full h-[520px] xl:h-[580px] object-cover"
                />
                {/* Decorative badge */}
                <div 
                  className={`absolute top-6 right-6 bg-white/90 backdrop-blur-sm rounded-2xl px-4 py-3 shadow-lg transition-all duration-[900ms] ease-out delay-200 ${
                    heroVisible 
                      ? 'opacity-100 translate-y-0' 
                      : 'opacity-0 translate-y-5'
                  }`}
                >
                  <p className="text-xs text-stone-500 italic font-serif">"Small Kittens,</p>
                  <p className="text-xs text-stone-500 italic font-serif">Big Happiness"</p>
                  <Heart className="w-4 h-4 text-[#E85D75] mt-1 mx-auto" />
                </div>
              </div>
              {/* Decorative elements */}
              <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-rose-100 rounded-full opacity-60 blur-2xl" />
              <div className="absolute -top-4 -right-4 w-32 h-32 bg-pink-100 rounded-full opacity-40 blur-3xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-12 bg-white border-y border-rose-100/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {trustIndicators.map((item) => {
              const IconComponent = item.icon;
              return (
                <div
                  key={item.title}
                  className="trust-card flex flex-col items-center text-center p-4"
                  data-testid={`trust-${item.title.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className="trust-icon w-14 h-14 bg-rose-50 rounded-full flex items-center justify-center mb-3">
                    <IconComponent className="w-6 h-6 text-[#E85D75]" />
                  </div>
                  <h3 className="text-sm font-semibold text-stone-800">{item.title}</h3>
                  <p className="text-xs text-stone-500">{item.subtitle}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Kittens Section */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-2 mb-4">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-semibold text-stone-900">
                Our Kittens
              </h2>
              <Heart className="w-6 h-6 text-[#E85D75]" />
            </div>
            <p className="text-base text-stone-600 max-w-2xl mx-auto">
              Meet our beautiful Scottish Fold and Scottish Straight kittens looking for their forever homes.
            </p>
          </div>

          {/* Filter Pills */}
          <div className="flex flex-wrap justify-center gap-2 mb-10">
            {['All', 'Scottish Fold', 'Scottish Straight', 'Available', 'Reserved'].map((filter, idx) => (
              <button
                key={filter}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                  idx === 0
                    ? 'bg-[#E85D75] text-white shadow-md shadow-[#E85D75]/25'
                    : 'bg-white text-stone-600 border border-stone-200 hover:border-rose-300 hover:text-[#E85D75]'
                }`}
              >
                {filter}
              </button>
            ))}
          </div>

          {/* Kittens Grid */}
          {featuredKittens.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
              {featuredKittens.map((kitten) => (
                <KittenCard key={kitten.id} kitten={kitten} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-white rounded-3xl border border-rose-100">
              <PawPrint className="w-12 h-12 text-rose-200 mx-auto mb-4" />
              <p className="text-stone-600">No kittens available at the moment.</p>
              <p className="text-sm text-stone-500 mt-2">Check back soon or contact us for updates!</p>
            </div>
          )}

          {featuredKittens.length > 0 && (
            <div className="text-center mt-10">
              <Link to="/kittens">
                <Button
                  data-testid="view-all-kittens-button"
                  variant="outline"
                  className="bg-white text-[#E85D75] border-2 border-[#E85D75] hover:bg-[#E85D75] hover:text-white rounded-full px-8 py-5 font-medium transition-all"
                >
                  View All Kittens <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Trust & Transparency Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {trustSections.map((section) => {
              const IconComponent = section.icon;
              return (
                <div
                  key={section.title}
                  className="group bg-[#FAF8F5] rounded-2xl p-6 hover:shadow-lg hover:shadow-rose-100/50 transition-all duration-300"
                >
                  <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-4 shadow-sm group-hover:bg-rose-50 transition-colors">
                    <IconComponent className="w-6 h-6 text-[#E85D75]" />
                  </div>
                  <h3 className="text-lg font-serif font-semibold text-stone-800 mb-2">
                    {section.title}
                  </h3>
                  <p className="text-sm text-stone-600 mb-4 leading-relaxed">
                    {section.description}
                  </p>
                  <Link
                    to={section.link}
                    className="inline-flex items-center text-sm font-medium text-[#E85D75] hover:text-[#D64560] transition-colors"
                  >
                    Learn More <ArrowRight className="w-4 h-4 ml-1" />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Google Reviews Section */}
      <GoogleReviews />

      {/* Final CTA */}
      <section className="py-20 lg:py-28 bg-gradient-to-br from-rose-50 to-[#FAF8F5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Heart className="w-10 h-10 text-[#E85D75] mx-auto mb-6" />
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-semibold text-stone-900 mb-6">
            Ready to Welcome a New Family Member?
          </h2>
          <p className="text-base sm:text-lg text-stone-600 mb-8 max-w-2xl mx-auto">
            Contact us today to learn more about our available kittens and our adoption process.
            We can't wait to help you find your perfect companion.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button
                data-testid="cta-contact-button"
                className="bg-[#E85D75] text-white hover:bg-[#D64560] shadow-lg shadow-[#E85D75]/25 rounded-full px-10 py-6 text-base font-medium transition-all transform hover:-translate-y-0.5"
              >
                Get in Touch
              </Button>
            </Link>
            <Link to="/kittens">
              <Button
                data-testid="cta-kittens-button"
                variant="outline"
                className="bg-white text-stone-800 border border-stone-200 hover:border-rose-300 hover:bg-rose-50/30 rounded-full px-10 py-6 text-base font-medium transition-all"
              >
                Browse Kittens
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
